import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as act from "../../store/actions/anonAutoForm";
import { setCanAdvance} from "../../store/actions/anonymousForms";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import Wizard from "../shared/wizard/Wizard";
import YourInformation from "./shared-pages/YourInformation";
import DateTime from "./shared-pages/DateTime";
import IncidentLocation from "./shared-pages/IncidentLocation";
import IncidentDetails from "./shared-pages/IncidentDetails";
import StatusBar from "../ui/status-bar/StatusBar";
import Witnesses from "./shared-pages/Witnesses";
import TheVehicle from "./auto-pages/TheVehicle";
import OtherVehicle from "./auto-pages/OtherVehicle";
import Driver from "./auto-pages/Driver";
import PoliceContacted from "./auto-pages/PoliceContacted";
import Property from "./auto-pages/Property";
import Injuries from "./auto-pages/Injuries";
import Documents from "./shared-pages/Documents";
import AutoClaimSummary from "./auto-pages/AutoClaimSummary";
import Styles from "./AnonForms.module.css";
import useDocuments from "../../hooks/useDocuments";
import inputUtilities from "../../utilities/forms/formInputUtilities";
import { confirmEmailValidator, createValidator, timeValidator, validateFieldset, validatePersonArray } from "../../utilities/forms/formValidationUtilities"; 
import { alphabeticTest, emailLengthProps, emailTest, freeFormTest, length100Props, length1333Props, length40Props, length50Props, noSpecialsTest, phoneProps, phoneTest, trueFalseRequired, yearProps, yearTest, zipProps, zipTest } from "../../utilities/validationUtilities";
import { Canada, US } from "../../constants/data";
import { BehalfOfOptions, userTypes, vehicleOwnerOptions, yesNoUnsure } from "../../constants/options";
import { concatenateName } from "../../utilities/dataUtilities";
import { colors } from "../../constants/colors";
import Passengers from "./auto-pages/Passengers";

export default function AnonAutoForm() {
  const dispatch = useDispatch();
  const lossCauses = useSelector((state) => state.forms.lossCauses).filter(
    (item) => item.Auto
  );
  const [page, setPage] = useState(0); //current
  const canAdvance = useSelector((state) => state.anonymousForms.canAdvance);
  const config = useSelector((state) => state.app.config);
  const documents = useSelector((state) => state.anonAutoForm.documents);

  const rows3Props = { rows: 3 };

  const fields = {
    contactFirstName: {
      label: "First Name",
      value: "",
      page: 0,
      editPage: 2,
      regex: alphabeticTest,
      additionalProps: length50Props,
    },
    contactLastName: {
      label: "Last Name",
      value: "",
      page: 0,
      editPage: 2,
      regex: alphabeticTest,
      additionalProps: length50Props,
    },
    contactEmail: {
      label: "Email",
      value: "",
      required: true,
      page: 0,
      editPage: 2,
      regex: emailTest,
      additionalProps: emailLengthProps,
    },
    confirmEmail: {
      label: "Confirm Email",
      value: "",
      required: true,
      page: 0,
      editPage: 2,
      regex: emailTest,
      additionalProps: emailLengthProps,
      fieldName: "confirmEmail",
    },
    contactPhone: {
      label: "Phone",
      value: "",
      required: true,
      page: 0,
      editPage: 2,
      regex: phoneTest,
      additionalProps: phoneProps,
    },
    userType: {
      label: "I am",
      value: userTypes[0].value,
      required: true,
      page: 0,
      editPage: 2,
    },
    onBehalfOf: {
      label: "On Behalf Of",
      value: "",
      required: true,
      page: 0,
      editPage: 2,
    },
    behalfOfFirst: {
      label: "First Name",
      value: "",
      page: 0,
      editPage: 2,
      regex: alphabeticTest,
      additionalProps: length50Props,
    },
    behalfOfLast: {
      label: "Last Name",
      value: "",
      required: true,
      page: 0,
      editPage: 2,
      regex: alphabeticTest,
      additionalProps: length50Props,
    },
    whereDate: {
      label: "Date",
      value: "",
      required: true,
      page: 1,
      editPage: 0,
      additionalProps: { min:"1950-01-01", max: new Date().toLocaleDateString("fr-ca") }
    },
    whereTime: {
      label: "Time",
      value: "",
      required: true,
      page: 1,
      editPage: 0,
      fieldName: "whereTime",
    },
    whereAddress: {
      label: "Address",
      value: "",
      page: 2,
      editPage: 3,
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    whereCity: {
      label: "City",
      value: "",
      page: 2,
      editPage: 3,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    whereState: {
      label: "State",
      value: "",
      page: 2,
      editPage: 3,
      required: true,
    },
    whereZipcode: {
      label: "Zip Code",
      value: "",
      page: 2,
      editPage: 3,
      regex: zipTest,
      additionalProps: zipProps,
    },
    whereCounty: {
      label: "County",
      value: "",
      page: 2,
      editPage: 3,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    whereCountry: {
      label: "Country",
      value: US.name,
      page: 2,
      editPage: 3,
    },
    driverFirstName: {
      label: "First Name",
      value: "",
      page: 3,
      editPage: 4,
      regex: alphabeticTest,
      additionalProps: length50Props,
    },
    driverLastName: {
      label: "Last Name",
      value: "",
      page: 3,
      editPage: 4,
      required: true,
      regex: alphabeticTest,
      additionalProps: length50Props,
    },
    driverIsInjured: {
      label: "Was the driver injured?",
      value: false,
      page: 3,
      editPage: 4,
    },
    whoIsTheInsured: {
      label: "Is this the insured?",
      value: true,
      page: 3,
      editPage: 4,
      required: true,
      fieldName: "whoIsTheInsured",
      validation: trueFalseRequired,
    },
    whoAddress: {
      label: "Address",
      value: "",
      page: 3,
      editPage: 4,
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    whoCity: {
      label: "City",
      value: "",
      page: 3,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    whoState: {
      label: "State",
      value: "",
      page: 3,
      editPage: 4,
      required: true,
    },
    whoCounty: {
      label: "County",
      value: "",
      page: 3,
      editPage: 4,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    whoZipcode: {
      label: "Zip Code",
      value: "",
      page: 3,
      editPage: 4,
      additionalProps: zipProps,
      regex: zipTest,
      required: true,
    },
    whatHappen: {
      label: "Describe the incident here",
      value: "",
      page: 4,
      editPage: 5,
      required: true,
      additionalProps: { rows: 12, ...length1333Props },
      regex: freeFormTest,
    },
    lossCause: {
      label: "Which of the following best describes the cause of loss?",
      value: "",
      displayValue: null,
      page: 4,
      editPage: 5,
      required: true,
    },
    policeAgencyContacted: {
      label: "Was a police agency contacted?",
      value: yesNoUnsure[2].value,
      page: 5,
      editPage: 13,
    },
    reportAgencyName: {
      label: "Agency Name",
      value: "",
      page: 5,
      editPage: 13,
      regex: freeFormTest,
    },
    reportNumber: {
      label: "Report Number",
      value: "",
      page: 5,
      editPage: 13,
      regex: freeFormTest,
    },
    witnessesPresent: {
      label: "Were there any witnesses?",
      value: yesNoUnsure[2].value,
      page: 6,
      editPage: 7,
    },
    vehicleIs: {
      label: "The vehicle is...",
      value: vehicleOwnerOptions[0].value,
      page: 7,
      editPage: 6,
      required: true,
    },
    vehicleYear: {
      label: "Year",
      value: "",
      page: 7,
      editPage: 6,
      additionalProps: yearProps,
      regex: yearTest,
    },
    vehicleMake: {
      label: "Car Make",
      value: "",
      page: 7,
      editPage: 6,
    },
    vehicleModel: {
      label: "Model",
      value: "",
      page: 7,
      editPage: 6,
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    vehicleVin: {
      label: "VIN",
      value: "",
      page: 7,
      editPage: 6,
      regex: freeFormTest,
    },
    currentLocation: {
      label: "Current Vehicle Location",
      value: "",
      page: 7,
      editPage: 6,
    },
    currentLocationVehicleAddress: {
      label: "Address",
      value: "",
      page: 7,
      editPage: 6,
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    currentLocationVehicleCity: {
      label: "City",
      value: "",
      page: 7,
      editPage: 6,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    currentLocationVehicleState: {
      label: "State",
      value: null,
      page: 7,
      editPage: 6,
    },
    currentLocationVehicleZip: {
      label: "Zip Code",
      value: "",
      page: 7,
      editPage: 6,
      additionalProps: zipProps,
      regex: zipTest,
    },
    vehicleDescribe: {
      label: "Damage",
      value: "",
      required: true,
      page: 7,
      editPage: 6,
      additionalProps: { 
        ...length1333Props,
        placeholder: "Describe the damage.",
        rows: 5,
       },
      regex: freeFormTest,
    },
    vehicleEstimateDamage: {
      label: "Estimate of damage",
      value: "",
      page: 7,
      editPage: 6,
      regex: freeFormTest,
    },
    vehicleDriveable: {
      label: "Is the vehicle drivable?",
      value: true,
      page: 7,
      editPage: 6,
    },
    rentalCompanyName: {
      label: "Company Name",
      value: "",
      required: true,
      page: 7,
      editPage: 6,
      regex: freeFormTest,
    },
    ownedFirstName: {
      label: "First Name",
      value: "",
      page: 7,
      editPage: 6,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    ownedLastName: {
      label: "Last Name",
      value: "",
      required: true,
      page: 7,
      editPage: 6,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    nonPolicyVehicleAddress: {
      label: "Street Address",
      value: "",
      page: 7,
      editPage: 6,
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    nonPolicyVehicleCity: {
      label: "City",
      value: "",
      page: 7,
      editPage: 6,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    nonPolicyVehicleState: {
      label: "State",
      value: "",
      page: 7,
      editPage: 6,
    },
    nonPolicyVehicleZipCode: {
      label: "Zip Code",
      value: "",
      page: 7,
      editPage: 6,
      additionalProps: zipProps,
      regex: zipTest,
    },
    nonPolicyVehiclePhone: {
      label: "Phone",
      value: "",
      page: 7,
      editPage: 6,
      additionalProps: phoneProps,
      regex: phoneTest,
    },
    otherVehicleInvolved: {
      label: "Were any other vehicles involved in the accident?",
      value: false,
      required: true,
      page: 8,
      editPage: 11,
      fieldName: "otherVehicleInvolved",
      validation: trueFalseRequired,
    },
    otherVehicleYear: {
      label: "Year",
      value: "",
      page: 8,
      editPage: 11,
      additionalProps: yearProps,
      regex: yearTest,
    },
    otherVehicleMake: {
      label: "Make",
      value: "",
      page: 8,
      editPage: 11,
    },
    otherVehicleModel: {
      label: "Model",
      value: "",
      page: 8,
      editPage: 11,
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    otherVehiclePlateNumber: {
      label: "Plate Number",
      value: "",
      page: 8,
      editPage: 11,
      additionalProps: length40Props,
      regex: freeFormTest,
    },
    otherVehicleState: {
      label: "State",
      value: "",
      page: 8,
      editPage: 11,
    },
    otherVehicleDescribe: {
      label: "Describe the Damage",
      value: "",
      page: 8,
      editPage: 11,
      additionalProps: {
        ...rows3Props,
        ...length1333Props,
        placeholder: "Please describe the damage to the other vehicle here.",
      },
      required: true,
      regex: freeFormTest,
    },
    otherVehicleDriverFirstName: {
      label: "First Name",
      value: "",
      page: 8,
      editPage: 11,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    otherVehicleDriverLastName: {
      label: "Last Name",
      value: "",
      page: 8,
      editPage: 11,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    otherVehicleDriverInjured: {
      label: "Is the driver injured?",
      value: false,
      page: 8,
      editPage: 11,
    },
    otherDriverIsOwner: {
      label: "Is this the owner?",
      value: true,
      page: 8,
      editPage: 11,
    },
    otherVehicleDriverAddress: {
      label: "Address",
      value: "",
      page: 8,
      editPage: 11,
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    otherVehicleDriverCity: {
      label: "City",
      value: "",
      page: 8,
      editPage: 11,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    otherVehicleDriverState: {
      label: "State",
      value: "",
      page: 8,
      editPage: 11,
    },
    otherVehicleDriverZipcode: {
      label: "Zip Code",
      value: "",
      page: 8,
      editPage: 11,
      additionalProps: zipProps,
      regex: zipTest,
    },
    otherVehicleDriverPhone: {
      label: "Phone",
      value: "",
      page: 8,
      editPage: 11,
      additionalProps: phoneProps,
      regex: phoneTest
    },
    otherVehicleOwnerFirstName: {
      label: "Owner's First Name",
      value: "",
      page: 8,
      editPage: 11,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    otherVehicleOwnerLastName: {
      label: "Owner's Last Name",
      value: "",
      page: 8,
      editPage: 11,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    otherVehicleOwnerAddress: {
      label: "Address",
      value: "",
      page: 8,
      editPage: 11,
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    otherVehicleOwnerCity: {
      label: "City",
      value: "",
      page: 8,
      editPage: 11,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    otherVehicleOwnerState: {
      label: "State",
      value: "",
      page: 8,
      editPage: 11,
    },
    otherVehicleOwnerZipcode: {
      label: "Zip Code",
      value: "",
      page: 8,
      editPage: 11,
      additionalProps: zipProps,
      regex: zipTest,
    },
    otherVehicleOwnerPhone: {
      label: "Phone",
      value: "",
      page: 8,
      editPage: 11,
      additionalProps: phoneProps,
      regex: phoneTest,
    },
    damageToProperty: {
      label: "Was there any other damage to property other than vehicles?",
      value: false,
      required: true,
      page: 9,
      editPage: 12,
      fieldName: "damageToProperty",
      validation: trueFalseRequired,
    },
    whoOwnsProperty: {
      label: "Do you know the owner of the property?",
      value: false,
      required: true,
      page: 9,
      editPage: 12,
      fieldName: "whoOwnsProperty",
      validation: trueFalseRequired,
    },
    propertyOwnerFirstName: {
      label: "Property Owner's First Name",
      value: "",
      page: 9,
      editPage: 12,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    propertyOwnerLastName: {
      label: "Property Owner's Last Name",
      value: "",
      page: 9,
      editPage: 12,
      additionalProps: length50Props,
      regex: alphabeticTest,
      required: true,
    },
    propertyOwnerAddress: {
      label: "Address",
      value: "",
      page: 9,
      editPage: 12,
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    propertyOwnerCity: {
      label: "City",
      value: "",
      page: 9,
      editPage: 12,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    propertyOwnerState: {
      label: "State",
      value: "",
      page: 9,
      editPage: 12,
    },
    propertyOwnerCounty: {
      label: "County",
      value: "",
      page: 9,
      editPage: 12,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    propertyOwnerZipcode: {
      label: "Zip Code",
      value: "",
      page: 9,
      editPage: 12,
      additionalProps: zipProps,
      regex: zipTest,
    },
    propertyOwnerCountry: {
      label: "Country",
      value: US.name,
      page: 9,
      editPage: 12,
    },
    propertyOwnerPhone: {
      label: "Phone",
      value: "",
      page: 9,
      editPage: 12,
      additionalProps: phoneProps,
      regex: phoneTest,
    },
    propertyOwnerDescribeProperty: {
      label: "Describe the property",
      value: "",
      page: 9,
      editPage: 12,
      additionalProps: { ...rows3Props, ...length1333Props },
      required: true,
      regex: freeFormTest,
    },
    propertyOwnerDamageProperty: {
      label: "Describe the damage to the property",
      value: "",
      page: 9,
      editPage: 12,
      additionalProps: { ...rows3Props, ...length1333Props },
      required: true,
      regex: freeFormTest,
    },
    propertyLocationAddress: {
      label: "Address",
      value: "",
      page: 9,
      editPage: 12,
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    propertyLocationCity: {
      label: "City",
      value: "",
      page: 9,
      editPage: 12,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    propertyLocationState: {
      label: "State",
      value: "",
      page: 9,
      editPage: 12,
    },
    propertyLocationZip: {
      label: "Zip Code",
      value: "",
      page: 9,
      editPage: 12,
      additionalProps: zipProps,
      regex: zipTest,
    },
    werePassengers: {
      label: "Were there any passengers in this vehicle?",
      value: yesNoUnsure[2].value,
      page: 10,
      editPage: 8,
    },
    pedestrianInjured: {
      label: "Were any pedestrians injured?",
      value: yesNoUnsure[2].value,
      page: 11,
      editPage: 10,
    },
    werePassengersOtherVehicle: {
      label: "Were there any passengers in this vehicle?",
      value: yesNoUnsure[2].value,
      page: 10,
      editPage: 9,
    },
  };

  const witness = {
    firstName: {
      label: "First Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    lastName: {
      label: "Last Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    witnessPhone: {
      label: "Phone",
      value: "",
      additionalProps: phoneProps,
      regex: phoneTest,
    }
  };

  const passenger = {
    firstName: {
      label: "First Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    lastName: {
      label: "Last Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    address: {
      label: "Address",
      value: "",
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    city: {
      label: "City",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    state: {
      label: "State",
      value: "",
      displayValue: null,
    },
    zipCode: {
      label: "Zip Code",
      value: "",
      additionalProps: zipProps,
      regex: zipTest,
    },
    phoneNumber: {
      label: "Phone Number",
      value: "",
      additionalProps: phoneProps,
      regex: phoneTest,
    },
    isInjured: {
      label: "Is this passenger injured?",
      value: false,
    },
  };

  const injured = {
    firstName: {
      label: "First Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    lastName: {
      label: "Last Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    }
  };

  //these 5 variables hold all of the form submission data
  let [witnessInfo, setWitnessInfo] = useState([]);
  let [pedestrianInfo, setPedestrianInfo] = useState([]);
  let [passengerInfo, setPassengerInfo] = useState([]);
  let [otherPassengerInfo, setOtherPassengerInfo] = useState([]);
  const [formFields, setFormFields] = useState(fields);
  const [validatorsAdded, setValidatorsAdded] = useState();
 const utilities = inputUtilities(formFields, setFormFields, Styles);

  const addValidatorsNames = (theFields, setFields, shouldDispatch = false) => {
    let newFields = { ...theFields };
    for (let key in newFields) {
      if (
        newFields[key].fieldName !== formFields.confirmEmail.fieldName &&
        newFields[key].fieldName !== formFields.whereTime.fieldName &&
        newFields[key].fieldName !== formFields.whoIsTheInsured.fieldName &&
        newFields[key].fieldName !== formFields.otherVehicleInvolved.fieldName &&
        newFields[key].fieldName !== formFields.damageToProperty.fieldName &&
        newFields[key].fieldName !== formFields.whoOwnsProperty.fieldName
      ) {
        const theField = newFields[key];
        newFields[key] = {
          ...theField,
          fieldName: key,
          validation: createValidator({
            ...theField.additionalProps,
            ...theField.regex,
            label: theField.label,
            required: theField.required,
          }),
        };
      } else if (newFields[key].fieldName === formFields.confirmEmail.fieldName) {
        const theField = newFields[key];
        newFields[key] = {
          ...theField,
          validation: confirmEmailValidator(newFields[key], formFields.contactEmail.value, formFields.confirmEmail.label),
        };
      } else if (newFields[key].fieldName === formFields.whereTime.fieldName) {
        const theField = newFields[key];
        newFields[key] = {
          ...theField,
          validation: timeValidator(newFields[key], formFields.whereDate.value),
        };
      }
    }
    shouldDispatch ? dispatch(setFields(newFields)) : setFields(newFields);
  }; //adds validation functions and fieldNames to fields objects

  useEffect(() => {
    if (formFields && !validatorsAdded) {
      addValidatorsNames(formFields, setFormFields);
      addValidatorsNames(injured, act.setInjuredObj, true);
      addValidatorsNames(passenger, act.setPassengerObj, true);
      addValidatorsNames(witness, act.setWitnessObj, true);
      setValidatorsAdded(true);
    }
  }, [formFields, validatorsAdded]);

  const reValidate = (field) => field.validation && field.value && utilities.handleFieldChange(field.value, field, true);

  useEffect(() => {
    formFields.confirmEmail.validation = confirmEmailValidator(formFields.confirmEmail, formFields.contactEmail.value, formFields.confirmEmail.label);
  }, [formFields.contactEmail.value])

  useEffect(() => {
    formFields.whereTime.validation = timeValidator(formFields.whereTime, formFields.whereDate.value);
  }, [formFields.whereDate.value])

  useEffect(() => {
    reValidate(formFields.confirmEmail);
  }, [formFields.confirmEmail.validation])

  useEffect(() => {
    reValidate(formFields.whereTime);
  }, [formFields.whereTime.validation])

  useEffect(() => {
    if (validatePage()) {
      dispatch(setCanAdvance(true));
    } else {
      dispatch(setCanAdvance(false));
    }
  }, [formFields, witnessInfo, pedestrianInfo, passengerInfo, otherPassengerInfo, page])

  useEffect(() => {
    if (validatorsAdded) {
      const theFields = {...formFields};
      if (formFields.whereCountry.value === Canada.name) {
        theFields.whereState.label = "Province";
      } else {
        theFields.whereState.label = "State";
      }
      theFields.whereState.value = "";
      setFormFields(theFields);
    }
  },[formFields.whereCountry.value, validatorsAdded])//handles change in province/state label based on location country

  useEffect(() => {
    if (validatorsAdded) {
      const theFields = {...formFields};
      if (formFields.propertyOwnerCountry.value === Canada.name) {
        theFields.propertyOwnerState.label = "Province";
      } else {
        theFields.propertyOwnerState.label = "State";
      }
      theFields.propertyOwnerState.value = "";
      setFormFields(theFields);
    }
  },[formFields.propertyOwnerCountry.value, validatorsAdded])//handles change in province/state label based on property owner country

  useEffect(() => {
    if (validatorsAdded) {
      const newFields = {...formFields};
      if (formFields.otherVehicleInvolved.value) {
        newFields.werePassengers.label = 
        `Were there any passengers in ${concatenateName(
          {
            firstName: formFields.driverFirstName,
            lastName: formFields.driverLastName
          })
        }'s vehicle?`
      } else {
        newFields.werePassengers.label = "Were there any passengers in the vehicle?";
      }
      setFormFields(newFields);
    }
  }, [formFields.otherVehicleInvolved.value, formFields.driverFirstName.value, formFields.driverLastName.value, validatorsAdded])

  useEffect(() => {
    if (validatorsAdded) {
      const newFields = {...formFields};
      newFields.werePassengersOtherVehicle.label =  `Were there any passengers in ${
        formFields.otherVehicleDriverLastName.value
        ? `${concatenateName({
            firstName: formFields.otherVehicleDriverFirstName,
            lastName: formFields.otherVehicleDriverLastName
            })}'s`
        : "the other"} vehicle?`
      setFormFields(newFields);
    }
  }, [formFields.otherVehicleDriverFirstName.value, formFields.otherVehicleDriverLastName.value, validatorsAdded])
  
  const validatePage = (editPage = null, displayErrors = false) => {
    let pageFields = Object.values(formFields)
      .map((obj, index) => {
        return { ...obj, index: index };
      });
    if (editPage) {
      pageFields = pageFields.filter((field) => field.editPage === editPage);
    } else {
      pageFields = pageFields.filter((field) => field.page === page);
    }
      if (page === 6 || editPage === 7) {
        return validateFieldset(pageFields, formFields, setFormFields, displayErrors) 
          && validatePersonArray(witnessInfo, setWitnessInfo, displayErrors);
      }

      if (page === 10) {
        return validateFieldset(pageFields, formFields, setFormFields, displayErrors)
          && validatePersonArray(passengerInfo, setPassengerInfo, displayErrors)
          && validatePersonArray(otherPassengerInfo, setOtherPassengerInfo, displayErrors);
      }

      if (page === 11) {
        return validateFieldset(pageFields, formFields, setFormFields, displayErrors)
         && validatePersonArray(pedestrianInfo, setPedestrianInfo, displayErrors)
      }
      if (editPage === 8) {
        return validateFieldset(pageFields, formFields, setFormFields, displayErrors)
          && validatePersonArray(passengerInfo, setPassengerInfo, displayErrors);
      }
      if (editPage === 9) {
        return validateFieldset(pageFields, formFields, setFormFields, displayErrors)
          && validatePersonArray(otherPassengerInfo, setOtherPassengerInfo, displayErrors);
      }
      if (editPage === 10) {
        return validateFieldset(pageFields, formFields, setFormFields, displayErrors)
          && validatePersonArray(pedestrianInfo, setPedestrianInfo, displayErrors);
      }
      if (formFields.userType.value !== userTypes[1].value) {
        pageFields = pageFields.filter(field => 
          field.fieldName !== formFields.onBehalfOf.fieldName && 
          field.fieldName !== formFields.behalfOfLast.fieldName
        );
      } else if (formFields.onBehalfOf.value !== BehalfOfOptions[1]) {
        pageFields = pageFields.filter(field => field.fieldName !== formFields.behalfOfLast.fieldName);
      }
      if (formFields.whoIsTheInsured.value) {
        pageFields = pageFields.filter(field => 
          field.fieldName !== formFields.whoState.fieldName && 
          field.fieldName !== formFields.whoZipcode.fieldName
        );
      }
      if (formFields.vehicleIs.value !== vehicleOwnerOptions[1].value) {
        pageFields = pageFields.filter(field => field.fieldName !== formFields.rentalCompanyName.fieldName);
      }
      if (formFields.vehicleIs.value !== vehicleOwnerOptions[2].value) {
        pageFields = pageFields.filter(field => field.fieldName !== formFields.ownedLastName.fieldName);
      }
      if (!formFields.otherVehicleInvolved.value) {
        pageFields = pageFields.filter(field => field.fieldName !== formFields.otherVehicleDescribe.fieldName);
      }
      if (!formFields.damageToProperty.value || !formFields.whoOwnsProperty.value) {
        pageFields = pageFields.filter(field => 
          field.fieldName !== formFields.propertyOwnerLastName.fieldName && 
          field.fieldName !== formFields.propertyOwnerDescribeProperty.fieldName && 
          field.fieldName !== formFields.propertyOwnerDamageProperty.fieldName
        );
      }
      return validateFieldset(pageFields, formFields, setFormFields, displayErrors);
    };

  const handleDocuments = useDocuments(documents, act.setAutoDocuments, true);

  // Wizard page handling
  const pages = [
    <YourInformation
      key="yourInfo"
      fields={formFields}
      utilities={utilities}
    />,
    <DateTime
      key="dateTime"
      fields={formFields}
      utilities={utilities}
    />,
    <IncidentLocation
      key="location"
      fields={formFields}
      utilities={utilities}
    />,
    <Driver
      key="driver"
      fields={formFields}
      utilities={utilities}
    />,
    <IncidentDetails
      key="details"
      fields={formFields}
      utilities={utilities}
      lossCauses={lossCauses}
    />,
    <PoliceContacted
    key="agency"
    fields={formFields}
    utilities={utilities}
    />,
    <Witnesses
      key="witnesses"
      witnesses={witnessInfo}
      fields={formFields}
      utilities={utilities}
      setWitnesses={setWitnessInfo}
    />,
    <TheVehicle
      key="vehicle"
      fields={formFields}
      utilities={utilities}
    />,
    <OtherVehicle
      key="otherVehicle"
      fields={formFields}
      utilities={utilities}
    />,
    <Property
      key="property"
      fields={formFields}
      utilities={utilities}
    />,
    <Passengers
      key="passengers"
      fields={formFields}
      utilities={utilities}
      passengers={passengerInfo}
      otherPassengers={otherPassengerInfo}
      setPassengers={setPassengerInfo}
      setOtherPassengers={setOtherPassengerInfo}
    />,
    <Injuries
      key="injuries"
      fields={formFields}
      utilities={utilities}
      pedestrians={pedestrianInfo}
      setPedestrians={setPedestrianInfo}
    />,
    <Documents
      key="documents"
      documents={documents}
      handleDocuments={handleDocuments}
      config={config}
    />,
    <AutoClaimSummary
      handleDocuments={handleDocuments}
      config={config}
      key="summary"
      fields={formFields}
      utilities={utilities}
      validatePage={validatePage}
      witnesses={witnessInfo}
      pedestrians={pedestrianInfo}
      passengers={passengerInfo}
      otherPassengers={otherPassengerInfo}
      setWitnesses={setWitnessInfo}
      setPedestrians={setPedestrianInfo}
      setPassengers={setPassengerInfo}
      setOtherPassengers={setOtherPassengerInfo}
      setFields={setFormFields}
    />,
  ];
  const lastPage = pages.length - 1;
  function next() {
    setPage(Math.min(page + 1, lastPage));
  }
  function previous() {
    setPage(Math.max(0, page - 1));
  }
  // end wizard page handlin

  return (
    <div className={Styles.container}>
      <div className={`${Styles.backArrowContainer} ${Styles.arrowContainer}`}>
        <button
          type="button"
          className={page === 0 ? Styles.noArrow : Styles.arrowButton}
          onClick={previous}
          aria-label="Next Page"
        >
          <div className={Styles.arrowCircle}>
            <FontAwesomeIcon
              className={`${Styles.arrow} ${Styles.backArrow}`}
              icon={faChevronLeft}
              size="2x"
            />
          </div>
          <p className={Styles.svgText}>BACK</p>
        </button>
      </div>

      <div className={Styles.screenBox}>
        <span className="redMessage">Fields marked with (*) are required</span>{" "}
        <br />
        <Wizard page={page}>{validatorsAdded && pages}</Wizard>
        <div className={Styles.buttonDiv}>
          <div className={Styles.navButton}>
            {page !== 0 && (
              <button
                type="button"
                className={page === 0 ? Styles.noArrow : Styles.arrowButton}
                onClick={previous}
                aria-label="Next Page"
              >
                <div className={Styles.arrowCircle}>
                  <FontAwesomeIcon
                    className={`${Styles.bottomArrow} ${Styles.backArrow}`}
                    icon={faChevronLeft}
                    size="2x"
                  />
                </div>
                <span className={Styles.svgText}>BACK</span>
              </button>
            )}
          </div>
          <div className={Styles.navButton}>
            {page !== lastPage && (
              <button
                type="button"
                className={
                  page === lastPage ? Styles.noArrow : Styles.arrowButton
                }
                disabled={canAdvance ? false : true}
                onClick={next}
                aria-label="Next Page"
              >
                <span
                  className={
                    canAdvance
                      ? Styles.svgText
                      : `${Styles.svgText} ${Styles.disabledSvgText}`
                  }
                >
                  NEXT
                </span>
                <div
                  className={
                    canAdvance
                      ? Styles.arrowCircle
                      : ` ${Styles.arrowCircle} ${Styles.disabledArrow}`
                  }
                >
                  <FontAwesomeIcon
                    className={`${Styles.bottomArrow} ${Styles.nextArrow}`}
                    icon={faChevronRight}
                    size="2x"
                  />
                </div>
              </button>
            )}
          </div>
        </div>
        <div className={Styles.statusBarDiv}>
          <StatusBar
            bgcolor={colors.primary}
            completed={page + 1}
            total={lastPage + 1}
            message={"Claim Progress"}
          />
        </div>
      </div>
      <div className={`${Styles.nextArrowContainer} ${Styles.arrowContainer}`}>
        <button
          type="button"
          className={page === lastPage ? Styles.noArrow : Styles.arrowButton}
          disabled={canAdvance ? false : true}
          onClick={next}
          aria-label="Next Page"
        >
          <div
            className={
              canAdvance
                ? Styles.arrowCircle
                : ` ${Styles.arrowCircle} ${Styles.disabledArrow}`
            }
          >
            <FontAwesomeIcon
              className={`${Styles.arrow} ${Styles.nextArrow}`}
              icon={faChevronRight}
              size="2x"
            />
          </div>
          <p
            className={
              canAdvance
                ? Styles.svgText
                : `${Styles.svgText} ${Styles.disabledSvgText}`
            }
          >
            NEXT
          </p>
        </button>
      </div>
    </div>
  );
}
